import { Fragment, useState, useMemo, useCallback, type ReactNode } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { useGetLlmModel } from '../../../hooks/use-llm-model';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/use-mobile';
import { useSubscriptionStore } from 'state/store/subscription';
import { PLANS } from 'constant/plan';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { MessageRole } from 'features/chat/types/chat';
import { useParams } from 'react-router-dom';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Box } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import deepseekv32 from 'assets/images/deepseekv3.2.png';
import gptoss from 'assets/images/gptoss120b.png';
import kimik from 'assets/images/kimik2.png';
import llama33 from 'assets/images/llama3.3.png';
import llama4maverick from 'assets/images/llama4maverick.png';
import qwen32 from 'assets/images/qwen32b.png';
import glm45 from 'assets/images/glm4.5.png';
import { getTranslatedModelMetadata, getRegionIcon, getRegionHostingInfo } from 'constant/model-metadata';
import { LLM_MODEL_REGIONS, type LlmModelRegion } from 'constant/llm-model';
import { isSyntheticAutoModel } from 'features/chat/utils/resolve-conversation-model';
import type { IModel } from 'features/chat/types/model.types';
import { cn } from 'lib/utils';







interface ModelSelectorProps {
  isDisabled?: boolean;
  /** Agent mode: show this label (the agent's model) and lock the dropdown. */
  overrideLabel?: string | null;
  overrideLoading?: boolean;
  overrideRegion?: LlmModelRegion;
}

const RegionBadge = ({
  region,
  variant = 'default',
}: {
  region?: LlmModelRegion;
  variant?: 'default' | 'selected';
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  if (!region) return null;

  const isDark = theme === 'dark';
  const useWhiteIcon = isDark && variant === 'selected';
  const regionIcon = getRegionIcon(region, { useWhiteIcon });
  const isGlobal = region === LLM_MODEL_REGIONS.GLOBAL;
  const hostingLabel = getRegionHostingInfo(region, t);

  return (
    <span
      title={hostingLabel}
      aria-label={hostingLabel}
      className="inline-flex h-4 w-4 items-center justify-center flex-shrink-0"
    >
      {regionIcon ? (
        <img src={regionIcon} alt="" className="h-4 w-4 rounded-full object-cover" />
      ) : null}
      {isGlobal ? (
        <Globe
          className={cn(
            'h-4 w-4',
            useWhiteIcon ? 'text-true-white' : 'text-sky-500'
          )}
        />
      ) : null}
    </span>
  );
};

const GroupHeading = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </div>
);

function ModelHoverCard({
  model,
  description,
  icon,
}: {
  model: IModel;
  description: string;
  icon: ReactNode;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const rawProvider = model.providerLabel;
  const meta = getTranslatedModelMetadata(model.id, t, {
    providerLabel: rawProvider,
    region: model.region,
  });
  const providerLabel = rawProvider ?? meta.provider;
  const hostingRegion = model.region ?? meta.region;
  const { hostingInfo } = meta;

  return (
    <div className="flex w-[280px] flex-col justify-between text-foreground">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center">{icon}</div>
          <div className="text-sm font-semibold leading-5 text-foreground">
            {providerLabel} / {model.name}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{description}</div>
        {model.paused && (
          <div className="text-xs text-warning-high-emphasis">
            {model.pausedReason ||
              t('MODEL_PAUSED_REASON', { defaultValue: 'This model is paused for you right now.' })}
          </div>
        )}
        {model.viaTopUp && !model.paused && (
          <div className="text-xs text-muted-foreground">
            {t('MODEL_VIA_TOP_UP_HINT', { defaultValue: "Runs on your organization's top-up credits." })}
          </div>
        )}
        {meta.capabilities.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('CAPABILITIES') || 'Capabilities'}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {meta.capabilities.map((cap) => {
                const CapIcon = cap.icon;
                return (
                  <div
                    key={cap.label}
                    className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-foreground"
                  >
                    <CapIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-foreground">{cap.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {hostingInfo && (
        <div className="mt-3 flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
          <RegionBadge region={hostingRegion} />
          <span>{hostingInfo}</span>
        </div>
      )}
    </div>
  );
}

export const ModelSelector = ({
  isDisabled: externalIsDisabled,
  overrideLabel,
  overrideLoading,
  overrideRegion,
}: ModelSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [hoveredModelId, setHoveredModelId] = useState<string | null>(null);

  const selectedModel = useModelStore((state) => state.selectedModel);
  const models = useModelStore((state) => state.models);
  const lastUsedModelId = useModelStore((state) => state.lastUsedModelId);
  const lastUsedViaSmart = useModelStore((state) => state.lastUsedViaSmart);
  const openNewChatWithModel = useModelStore((state) => state.openNewChatWithModel);
  const userChangedModel = useModelStore((state) => state.userChangedModel);
  const setActiveConversationModelName = useChatStore(
    (state) => state.setActiveConversationModelName
  );

  const { conversationId } = useParams();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { isLoading, isFetching, refetch } = useGetLlmModel();

  const shouldDisableDropdown = useChatStore((state) => {
    if (!conversationId) return false;
    const conv = state.conversations[conversationId];
    const streamingMessage = conv?.messages.find(
      (msg) => msg.recordId === conv?.streamingAssistantMessageId
    );
    return (
      streamingMessage?.role === MessageRole.ASSISTANT ||
      (conv?.activeStreamsMap?.size ?? 0) > 0 ||
      conv?.assistantResponseRemaining !== 0
    );
  });

  const isStreaming =
    externalIsDisabled ||
    (shouldDisableDropdown && !!conversationId);

  const currentPlan = useSubscriptionStore((state) => state.currentPlan);
  // No subscription service for this tenant → treat absent plan as UNLIMITED
  // so the model dropdown still renders.
  const effectivePlanName = currentPlan?.planCode ?? PLANS.UNLIMITED;

  const memoizedModels = models;
  const isModelLoading = (isLoading || isFetching) && memoizedModels.length === 0;

  const displayedModels = useMemo(
    () => memoizedModels.slice(0, visibleCount),
    [memoizedModels, visibleCount]
  );

  const displayName = useMemo(() => {
    if (overrideLoading) return '…';
    if (overrideLabel) return overrideLabel;
    if (!selectedModel?.name) return '';
    return selectedModel.name;
  }, [overrideLabel, overrideLoading, selectedModel?.name]);

  const lastUsedDescription =
    lastUsedViaSmart && lastUsedModelId && !isSyntheticAutoModel(lastUsedModelId)
      ? t('MODEL_DESC_AUTO_LAST_USED', {
          model: lastUsedModelId,
          defaultValue: `Last used model: ${lastUsedModelId}`,
        })
      : null;
  const selectedDescription =
    !overrideLabel && selectedModel?.id === 'auto' && lastUsedDescription
      ? lastUsedDescription
      : selectedModel
        ? getTranslatedModelMetadata(selectedModel.id, t, {
            providerLabel: selectedModel.providerLabel,
            region: selectedModel.region,
          }).description
        : null;

  const handleModelSelect = useCallback(
    (modelId: string) => {
      if (selectedModel?.id !== modelId) {
        const model = memoizedModels.find((m) => m.id === modelId);
        if (model) {
          setActiveConversationModelName(model.id);
          openNewChatWithModel(model);
          userChangedModel(true);
        }
      }
      setOpen(false);
    },
    [
      memoizedModels,
      openNewChatWithModel,
      selectedModel?.id,
      setActiveConversationModelName,
      userChangedModel,
    ]
  );

  // 🔒 Hook declared BEFORE early return (eslint safe)
  const ensureScrollable = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      if (
        el.scrollHeight <= el.clientHeight &&
        visibleCount < memoizedModels.length
      ) {
        setVisibleCount((prev) =>
          Math.min(prev + 5, memoizedModels.length)
        );
      }
    },
    [visibleCount, memoizedModels.length]
  );

  if (effectivePlanName === PLANS.NOPLAN) {
    return null;
  }

  if (isModelLoading) {
    return (
      <div className="flex items-center">
        <Skeleton className="h-8 w-32 rounded-full bg-muted dark:bg-primary-foreground/20" />
      </div>
    );
  }

  if (memoizedModels.length === 0) {
    return null;
  }

  const modelIconMap: Record<string, string> = {
    'DeepSeek-V3-0324': deepseekv32,
    'Qwen3-32B': qwen32,
    'Qwen2.5-VL-72B-Instruct': qwen32,
    'Llama-3.3-70B-Instruct-fast': llama33,
    'Llama-4-Maverick': llama4maverick,
    'gpt-oss-120b': gptoss,
    'Kimi-K2-Instruct': kimik,
    'GLM-4.5': glm45,
  };

  const modelsIconViewer = (model: any) => {
    const icon = modelIconMap[model.id];
    if (icon) {
      return <img className="w-4 h-4" src={icon} alt={model.name} />;
    }
    return <Box className="h-4 w-4" />;
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open ? false : undefined}>
        <DropdownMenu
          open={isStreaming ? false : open}
          onOpenChange={(isOpen) => {
            if (isStreaming) return;
            setOpen(isOpen);
            // A model can be paused mid-session, right after a turn, so the list is read again.
            if (isOpen) refetch?.();
            if (!isOpen) {
              setVisibleCount(5);
              setHoveredModelId(null);
            }
          }}
          modal={false}
        >
          <TooltipTrigger asChild>
            <div className="min-w-0 max-w-36 sm:max-w-40 md:max-w-full">
              <DropdownMenuTrigger asChild disabled={isStreaming}>
                <Button
                  variant="ghost"
                  disabled={isStreaming}
                  className={`max-w-full min-w-0 w-auto justify-between transition-colors duration-200 h-auto px-0 py-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none hover:bg-transparent ${theme === 'dark' ? 'text-true-white' : ''} ${isStreaming ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {/* <Box
                    className={`sm:h-4 h-3 sm:w-4 w-3 flex-shrink-0 ${theme === 'dark'
                      ? 'text-true-white'
                      : 'text-grey-900'
                      }`}
                  /> */}
                  <span
                    className={`text-sm font-normal leading-5 overflow-hidden text-ellipsis whitespace-nowrap min-w-0 ${theme === 'dark'
                      ? 'text-true-white'
                      : 'text-grey-900'
                      }`}
                  >
                    {displayName}
                  </span>
                  <RegionBadge
                    region={overrideLabel || overrideLoading ? overrideRegion : selectedModel?.region}
                  />
                  <ChevronDown
                    className={`h-3 w-3 flex-shrink-0 2xl:h-4 2xl:w-4 ${theme === 'dark'
                      ? 'text-true-white'
                      : 'text-grey-900'
                      }`}
                  />
                </Button>
              </DropdownMenuTrigger>
            </div>
          </TooltipTrigger>

          <DropdownMenuContent
            align="start"
            className={`p-0 bg-background border-[0.5px] ${theme === 'dark' ? 'border-black-500' : 'border-border/60'
              } shadow-lg rounded-xl overflow-visible`}
          >
            <div className="relative">
              {/* Hover detail panel — floats on the left of the list with a gap */}
              {(() => {
                if (!hoveredModelId) return null;
                const focusedModel = memoizedModels.find((m) => m.id === hoveredModelId);
                if (!focusedModel) return null;
                const description =
                  focusedModel.id === 'auto' && lastUsedDescription
                    ? lastUsedDescription
                    : getTranslatedModelMetadata(focusedModel.id, t, {
                        providerLabel: focusedModel.providerLabel,
                        region: focusedModel.region,
                      }).description;
                return (
                  <div
                    className={`absolute top-0 right-full z-50 mr-2 hidden rounded-xl border-[0.5px] bg-background p-4 shadow-lg md:flex ${theme === 'dark' ? 'border-black-500' : 'border-border/60'
                      }`}
                    onMouseEnter={() => setHoveredModelId(focusedModel.id)}
                  >
                    <ModelHoverCard
                      model={focusedModel}
                      description={description}
                      icon={modelsIconViewer(focusedModel)}
                    />
                  </div>
                );
              })()}

              {/* Model list */}
              <div
                ref={ensureScrollable}
                className="flex w-64 max-h-[340px] flex-col overflow-y-auto p-2 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1"
                onMouseLeave={() => setHoveredModelId(null)}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
                    setVisibleCount((prev) =>
                      Math.min(prev + 5, memoizedModels.length)
                    );
                  }
                }}
              >
                <GroupHeading>{t('MODELS') || 'Models'}</GroupHeading>
                <div className="flex flex-col gap-3">
                  {displayedModels.map((model, index) => {
                  const isSelected = selectedModel?.id === model.id;
                  const isFirstCustom = model.isCustom && !displayedModels[index - 1]?.isCustom;
                  return (
                    <Fragment key={model.id}>
                    {isFirstCustom && (
                      <GroupHeading>{t('KLAX_MODELS', { defaultValue: 'Klax Models' })}</GroupHeading>
                    )}
                    <DropdownMenuItem
                      className={`h-5 min-h-5 shrink-0 overflow-visible p-0 rounded-md ${
                        theme === 'dark' ? 'focus:bg-muted' : 'focus:bg-accent/70'
                      } ${isSelected ? 'bg-muted' : ''}`}
                      onMouseEnter={() => setHoveredModelId(model.id)}
                      onFocus={() => setHoveredModelId(model.id)}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleModelSelect(model.id);
                      }}
                    >
                      <div
                        className={`flex h-5 items-center justify-between w-full px-2 cursor-pointer rounded-md transition-colors ${
                          theme === 'dark' ? 'hover:bg-muted' : 'hover:bg-accent/70'
                        } ${
                          isSelected
                            ? theme === 'dark'
                              ? 'bg-muted text-true-white'
                              : 'bg-muted text-grey-900'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {modelsIconViewer(model)}
                          </div>
                          <div
                            className={cn(
                              'text-sm font-normal leading-5 truncate',
                              model.paused && 'opacity-50'
                            )}
                          >
                            {model.name}
                          </div>
                        </div>
                        <div className="ml-2 flex items-center gap-1.5 flex-shrink-0">
                          {model.paused && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                              {t('MODEL_PAUSED', { defaultValue: 'Paused' })}
                            </span>
                          )}
                          {model.viaTopUp && !model.paused && (
                            <span
                              title={t('MODEL_VIA_TOP_UP_HINT', { defaultValue: "Runs on your organization's top-up credits." })}
                              className="rounded bg-muted px-1.5 py-0.5 text-3xs font-medium uppercase tracking-wide text-muted-foreground"
                            >
                              {t('MODEL_VIA_TOP_UP', { defaultValue: 'Top-up credits' })}
                            </span>
                          )}
                          <RegionBadge region={model.region} />
                        </div>
                      </div>
                    </DropdownMenuItem>
                    </Fragment>
                  );
                })}
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <TooltipContent
          className={`border-[0.5px] bg-background p-4 text-foreground shadow-lg ${theme === 'dark' ? 'border-black-500' : 'border-border/60'}`}
        >
          {isStreaming ? (
            <div className="text-xs py-1">
              {externalIsDisabled
                ? (t('MODEL_LOCKED_FOR_ASSISTANT') || 'Model selection is locked for this GPT')
                : t('CANT_CHANGE_MODEL_WHILE_STREAMING')
              }
            </div>
          ) : selectedModel ? (
            <ModelHoverCard
              model={selectedModel}
              description={selectedDescription || ''}
              icon={modelsIconViewer(selectedModel)}
            />
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};